import {
  Box,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Stack,
  Text,
} from "@chakra-ui/react";
import { MessageThreadFragment, UserRoleType } from "gql/graphql";
import { BellOutlineIcon, BellOutlineSlashIcon } from "modules/icons";
import { useUsers } from "modules/providers/users-provider";
import UserAvatar from "modules/_shared/user-avatar";
import UserName from "modules/_shared/user-name";
import React, { memo, ReactNode, useMemo } from "react";
import colors from "app/colors";
import NewChatButton from "./buttons/new-chat-button";
import NewMessageThreadButton from "./buttons/new-message-thread-button";
import ViewUserProfileButton from "./view-user-profile-button";
import UserPopoverOutOfOfficeInfo from "modules/_shared/user-popover-out-of-office-info";
import MessageThreadRemoveUserButton from "../messaging/_shared/buttons/message-thread-remove-user-button";
import NewTaskButton from "./buttons/new-task-button";
import VideoChatButton from "./buttons/video-chat-button";

interface IProps {
  messageThread: MessageThreadFragment;
  userId: string;
  children: ReactNode;
}

const UserPopover = ({ messageThread, userId, children }: IProps) => {
  const { usersById } = useUsers();

  const user = useMemo(() => usersById[userId], [userId, usersById]);
  const isNotifications = useMemo(
    () => messageThread.notificationUserIdList.includes(userId),
    [messageThread.notificationUserIdList, userId],
  );

  const displayNotificationOnIcon = useMemo(() => {
    if (!user) return false;
    return isNotifications && user.roleType !== UserRoleType.Email;
  }, [isNotifications, user]);

  const displayNotificationOffIcon = useMemo(() => {
    if (!user) return false;
    return !isNotifications && user.roleType !== UserRoleType.Email;
  }, [isNotifications, user]);

  if (!user) return null;

  return (
    <Popover key={userId} trigger="hover" placement="bottom-start" isLazy>
      <PopoverTrigger>
        {/* lineHeight={1} deals with a height issue with small avatars */}
        <Box lineHeight={1}>{children}</Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent onClick={(e) => e.stopPropagation()}>
          <PopoverArrow />
          <PopoverBody>
            <Stack w="full" spacing={2} alignItems="flex-start">
              <HStack w="full">
                <UserAvatar
                  userId={userId}
                  size="md"
                  displayOnlineIndicator={false}
                />
                {user.active ? (
                  <>
                    <Text color={colors.blackSecondary} fontWeight={700}>
                      <UserName userId={userId} />
                    </Text>
                    {displayNotificationOnIcon && (
                      <BellOutlineIcon boxSize={4} fill={colors.gray50} />
                    )}
                    {displayNotificationOffIcon && (
                      <BellOutlineSlashIcon boxSize={4} fill={colors.gray50} />
                    )}
                  </>
                ) : (
                  <Text color={colors.grayPrimary} fontStyle="italic">
                    <UserName userId={userId} />
                  </Text>
                )}
              </HStack>
              <UserPopoverOutOfOfficeInfo user={user} />
              <HStack spacing={4} px={2} alignSelf="center">
                <ViewUserProfileButton userId={userId} />
                <VideoChatButton
                  messageThread={messageThread}
                  circular
                  hasText
                  circularButtonSize={6}
                  circularIconSize={4}
                />
                <NewChatButton userId={userId} textAlignment="bottom" />
                <NewMessageThreadButton
                  userId={userId}
                  textAlignment="bottom"
                />
                <NewTaskButton userId={userId} textAlignment="bottom" />
              </HStack>
              <MessageThreadRemoveUserButton
                messageThread={messageThread}
                userId={userId}
              />
            </Stack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default memo(UserPopover);
